import PropTypes from "prop-types";

// Material UI Components
import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";

// Core Components
import MDCTooltip from "./MDCTooltip";

const useStyles = makeStyles((theme) => ({
  button: {
    "&.MuiButton-contained": {
      fontFamily: theme.palette.typography.fontFamily,
      fontSize: "1.125rem",
      textTransform: "initial",
      borderRadius: "5px",
      border: `solid 2px ${theme.palette.MDCColors.color23}`,
      padding: ".4rem 1rem",
      borderRadius: ".5rem" || "unset",
      marginTop: ".625rem",
      "&:hover": {
        borderColor: theme.palette.MDCColors.color23,
      },
      background: theme.palette.MDCColors.color23,
      marginRight: ".125rem",
      transition: "all 0.3s ease",
      "&:hover": {
        background: theme.palette.MDCColors.color33,
        border: `solid 2px ${theme.palette.MDCColors.color23}`,
      },
      color: theme.palette.MDCColors.black,
      "&:not(:disabled):hover": {
        color: theme.palette.MDCColors.black,
      },
      "&:disabled": {
        pointerEvents: "unset",
        cursor: "not-allowed",
        border: `solid 2px ${theme.palette.MDCColors.color18}`,
        "&:hover": {
          borderColor: theme.palette.MDCColors.color18,
        },
        color: theme.palette.MDCColors.color18,
        opacity: 1,
        background: theme.palette.MDCColors.color21,
        transition: "all 0.3s ease",
        "&:hover": {
          background: theme.palette.MDCColors.color21,
        },
      },
    },
    "&.MuiButton-outlined": {
      fontFamily: theme.palette.typography.fontFamily,
      fontSize: (props) => (props?.size === "small" ? ".875rem" : "1.125rem"),
      textTransform: "initial",
      borderRadius: "5px",
      border: `solid 2px ${theme.palette.MDCColors.color23}`,
      marginRight: ".5rem",
      padding: ".4rem 1rem",
      borderRadius: ".5rem" || "unset",
      marginTop: (props) => (props?.removeMarginTop ? "0" : ".625rem"),
      "&:hover": {
        borderColor: theme.palette.MDCColors.color23,
      },
      background: theme.palette.MDCColors.white,
      transition: "all 0.3s ease",
      "&:hover": {
        background: theme.palette.MDCColors.color33,
        border: `solid 2px ${theme.palette.MDCColors.color23}`,
      },
      color: theme.palette.MDCColors.black,
      "&:not(:disabled):hover": {
        color: theme.palette.MDCColors.black,
      },
      "&:disabled": {
        pointerEvents: "unset",
        cursor: "not-allowed",
        border: `solid 2px ${theme.palette.MDCColors.color18}`,
        "&:hover": {
          borderColor: theme.palette.MDCColors.color18,
        },
        color: theme.palette.MDCColors.color18,
        opacity: 1,
        background: theme.palette.MDCColors.color21,
        transition: "all 0.3s ease",
        "&:hover": {
          background: theme.palette.MDCColors.color21,
        },
      },
      "& .MuiButton-startIcon": {
        marginRight: (props) => (props?.size === "small" ? "16px" : ""),
        marginLeft: (props) => (props?.size === "small" ? "-10px" : ""),
      },
    },
    "&.MuiButton-text": {
      fontFamily: theme.palette.typography.fontFamily,
      fontSize: "1.125rem",
      textTransform: "initial",
      color: theme.palette.MDCColors.black,
      opacity: 1,
      // borderLeft: `2px solid ${theme.palette.MDCColors.color21}`,
      padding: "0.75rem",
      fontWeight: 400,
      "&:not(:disabled):hover": {
        color: theme.palette.MDCColors.black,
        backgroundColor: theme.palette.MDCColors.color35,
      },
      "&:disabled": {
        pointerEvents: "unset",
        cursor: "not-allowed",
      },
    },
    "&.MuiButton-simpleText": {
      fontFamily: theme.palette.typography.fontFamily,
      fontSize: "1.125rem",
      textTransform: "initial",
      color: theme.palette.MDCColors.black,
      opacity: 1,
      padding: "0.75rem",
      fontWeight: 400,
      backgroundColor: theme.palette.MDCColors.cardBackgroundColor,
      "&:not(:disabled):hover": {
        color: theme.palette.MDCColors.black,
        backgroundColor: theme.palette.MDCColors.cardBackgroundColor,
      },
    },
    "&.MuiButton-helpDesk": {
      fontFamily: theme.palette.typography.fontFamily,
      fontSize: "1.125rem",
      textTransform: "initial",
      borderRadius: "3rem",
      border: `solid 2px ${theme.palette.MDCColors.color23}`,
      padding: ".7rem 1rem",
      transform: "translateY(-0.7rem)",
      "&:hover": {
        borderColor: theme.palette.MDCColors.color23,
      },
      background: theme.palette.MDCColors.color23,
      marginRight: ".125rem",
      transition: "all 0.3s ease",
      "&:hover": {
        background: theme.palette.MDCColors.color23,
        border: `solid 2px ${theme.palette.MDCColors.color23}`,
      },
      color: theme.palette.MDCColors.white,
      "&:not(:disabled):hover": {
        color: theme.palette.MDCColors.white,
      },
    },
    "&.MuiButton-warningOutlined": {
      fontFamily: theme.palette.typography.fontFamily,
      fontSize: "1.125rem",
      textTransform: "initial",
      borderRadius: "5px",
      border: `solid 2px ${theme.palette.MDCColors.color34}`,
      marginRight: ".5rem",
      padding: ".4rem 1rem",
      borderRadius: ".5rem" || "unset",
      marginTop: (props) => (props?.removeMarginTop ? "0" : ".625rem"),
      "&:hover": {
        borderColor: theme.palette.MDCColors.color34,
      },
      background: theme.palette.MDCColors.white,
      transition: "all 0.3s ease",
      "&:hover": {
        border: `solid 2px ${theme.palette.MDCColors.color34}`,
      },
      color: theme.palette.MDCColors.black,
      "&:not(:disabled):hover": {
        color: theme.palette.MDCColors.black,
      },
      "&:disabled": {
        pointerEvents: "unset",
        cursor: "not-allowed",
        border: `solid 2px ${theme.palette.MDCColors.color18}`,
        "&:hover": {
          borderColor: theme.palette.MDCColors.color18,
        },
        color: theme.palette.MDCColors.color18,
        opacity: 1,
        background: theme.palette.MDCColors.color21,
        transition: "all 0.3s ease",
        "&:hover": {
          background: theme.palette.MDCColors.color21,
        },
      },
    },
  },
}));

const MDCButton = (props) => {
  const {
    label,
    onClick,
    disabled,
    variant,
    endIcon,
    className,
    startIcon,
    isSpanLabel,
    spanClassName,
  } = props;

  const { button } = useStyles(props);

  return (
    <MDCTooltip title={isSpanLabel ? label : ""}>
      <Button
        variant={variant}
        onClick={onClick}
        startIcon={startIcon}
        endIcon={endIcon}
        className={[button, className].join(" ")}
        disabled={disabled}
      >
        {isSpanLabel ? <span className={spanClassName}>{label}</span> : label}
      </Button>
    </MDCTooltip>
  );
};

MDCButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  disbled: PropTypes.bool,
};

export default MDCButton;
